import { Fragment, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';

/**
 * Patrón "Tables → Cards" (US-073).
 *
 * - viewport < breakpoint → tarjetas / lista (sin scroll horizontal)
 * - viewport ≥ breakpoint → tabla (cuando aporta comparar columnas)
 *
 * Ambas vistas se renderizan y se alternan por CSS (Tailwind). SSR-safe; no
 * mide el ancho en JS. Filtros y paginación viven en la page.
 *
 * El sort lo maneja la page, pero el control tiene que existir en las DOS
 * vistas: en tabla va en el encabezado de columna y en cards ese encabezado no
 * se renderiza, así que el componente ofrece su propio control (ver `sort`).
 *
 * Listo para adoptar en Asistentes (#44) sin reescribir este archivo.
 * Profesionales (#40), Clínicas, Movimientos y Pacientes ya lo usan.
 * Ver docs/PATRON_TABLES_TO_CARDS.md.
 */

/** Breakpoints soportados. A partir de este ancho se muestra la tabla. */
export type ResponsiveBreakpoint = 'sm' | 'md' | 'lg';

/** Alineación de una columna (afecta header y celda). */
export type ColumnAlign = 'left' | 'center' | 'right';

/** Orden aplicado: qué columna y en qué dirección. */
export interface ResponsiveSort {
  /** `key` de la columna por la que se ordena. */
  key: string;
  direction: 'asc' | 'desc';
}

/** Definición de una columna de la tabla (vista >= breakpoint). */
export interface ResponsiveColumnDef<Row> {
  /** Identificador estable de la columna (sirve de React key). */
  key: string;
  /** Encabezado de la columna. */
  header: ReactNode;
  /** Cómo renderizar el valor de la celda para una fila dada. */
  cell: (row: Row) => ReactNode;
  /** Alineación del contenido. Por defecto 'left'. */
  align?: ColumnAlign;
  /** Clases extra opcionales para el <th>. */
  headerClassName?: string;
  /** Clases extra opcionales para el <td>. */
  cellClassName?: string;
  /** Si true, la columna se ofrece en el control de orden de la vista cards. */
  sortable?: boolean;
  /**
   * Texto plano para el control de orden de cards. Necesario cuando `header`
   * es un nodo (p. ej. un botón de sort) y no se puede reutilizar como opción.
   */
  sortLabel?: string;
  /** Ancho de columna (p. ej. `18%`) cuando `pinHeader` alinea header y body. */
  width?: string;
}

export interface ResponsiveDataListProps<Row> {
  /** Datos a mostrar. */
  rows: Row[];
  /** Devuelve una key estable por fila (obligatorio para React). */
  keyField: (row: Row) => string;
  /** Columnas de la vista tabla. */
  columns: ResponsiveColumnDef<Row>[];
  /**
   * Cómo dibujar una fila en la vista card (< breakpoint). Opcional: si no se
   * pasa, se arma automáticamente una lista "etiqueta: valor" con las columnas.
   */
  renderCard?: (row: Row) => ReactNode;
  /**
   * Extra en el <tbody> después de cada fila (p. ej. un <tr> de sucursales
   * expandidas). Solo vista tabla; en cards el expand va dentro de renderCard.
   */
  renderAfterRow?: (row: Row) => ReactNode;
  /**
   * Ancho a partir del cual se usa tabla. Por defecto `lg` (1024px) para que
   * un celular en horizontal (~667–932px) siga en cards y no en tabla
   * con scroll de costado.
   */
  breakpoint?: ResponsiveBreakpoint;
  /** Si true, se muestra el slot de carga (gana sobre empty y filas). */
  loading?: boolean;
  /** Override del estado de carga. Default: LoadingBlock. */
  loadingSlot?: ReactNode;
  /** Mensaje cuando no hay filas. */
  emptyMessage?: ReactNode;
  /** Alias de emptyMessage; si ambos existen, gana emptySlot. */
  emptySlot?: ReactNode;
  /** Nombre accesible de la tabla. */
  ariaLabel?: string;
  /** Click opcional sobre una fila/card completa. */
  onRowClick?: (row: Row) => void;
  /** Orden vigente. Junto con `onSortChange` habilita el control en cards. */
  sort?: ResponsiveSort;
  /** Se llama con el orden pedido desde el control de cards. */
  onSortChange?: (next: ResponsiveSort) => void;
  /** Clases extra para el contenedor raíz. */
  className?: string;
  /**
   * Deja los títulos de columna fijos y scrollea solo las filas.
   * Usar junto con una page `panel-page--fixed` para que también queden
   * fijos los filtros. Sin sticky: no recorta la fila de arriba.
   */
  pinHeader?: boolean;
}

/**
 * Mapa estático de breakpoint → clases Tailwind. Debe ser estático porque
 * Tailwind no puede resolver clases construidas dinámicamente en runtime.
 */
const TABLE_VISIBILITY: Record<ResponsiveBreakpoint, string> = {
  sm: 'hidden sm:block',
  md: 'hidden md:block',
  lg: 'hidden lg:block',
};

const TABLE_PINNED: Record<ResponsiveBreakpoint, string> = {
  sm: 'hidden sm:flex sm:flex-col',
  md: 'hidden md:flex md:flex-col',
  lg: 'hidden lg:flex lg:flex-col',
};

const CARDS_VISIBILITY: Record<ResponsiveBreakpoint, string> = {
  sm: 'sm:hidden',
  md: 'md:hidden',
  lg: 'lg:hidden',
};

const ALIGN_CLASS: Record<ColumnAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

function emptyContent(emptySlot: ReactNode | undefined, emptyMessage: ReactNode | undefined): ReactNode {
  return emptySlot ?? emptyMessage ?? 'Sin datos.';
}

/**
 * Lista de datos responsive: cards en mobile, tabla en pantallas grandes.
 */
export function ResponsiveDataList<Row>({
  rows,
  keyField,
  columns,
  renderCard,
  renderAfterRow,
  breakpoint = 'lg',
  loading = false,
  loadingSlot,
  emptyMessage,
  emptySlot,
  ariaLabel,
  onRowClick,
  sort,
  onSortChange,
  className = '',
  pinHeader = false,
}: ResponsiveDataListProps<Row>) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={className} data-testid="responsive-data-list-loading">
        {loadingSlot ?? <LoadingBlock minHeightClassName="min-h-[8rem]" />}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className={`rounded-xl border border-[var(--bi-line)] bg-white/80 p-4 text-sm text-slate-500 dark:bg-white/5 dark:text-slate-400 ${className}`}
        data-testid="responsive-data-list-empty"
      >
        {emptyContent(emptySlot, emptyMessage)}
      </div>
    );
  }

  const defaultCard = (row: Row) => (
    <dl className="space-y-1">
      {columns.map((col) => (
        <div key={col.key} className="flex items-start justify-between gap-3">
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {col.header}
          </dt>
          <dd className="text-sm text-slate-900 dark:text-slate-100">{col.cell(row)}</dd>
        </div>
      ))}
    </dl>
  );

  const card = renderCard ?? defaultCard;

  const handleRowKeyDown = (row: Row, event: KeyboardEvent<HTMLElement>) => {
    if (!onRowClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(row);
    }
  };

  const sortableColumns = columns.filter((col) => col.sortable);
  const activeSort: ResponsiveSort = sort ?? {
    key: sortableColumns[0]?.key ?? '',
    direction: 'desc',
  };
  const isAsc = activeSort.direction === 'asc';
  const headerRow = (
    <tr>
      {columns.map((col) => (
        <th
          key={col.key}
          scope="col"
          style={col.width ? { width: col.width } : undefined}
          className={`px-3 py-2.5 font-semibold ${ALIGN_CLASS[col.align ?? 'left']} ${col.headerClassName ?? ''}`}
        >
          {col.header}
        </th>
      ))}
    </tr>
  );
  const bodyRows = rows.map((row) => (
    <Fragment key={keyField(row)}>
      <tr
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        onKeyDown={onRowClick ? (event) => handleRowKeyDown(row, event) : undefined}
        tabIndex={onRowClick ? 0 : undefined}
        className={`border-b border-[var(--bi-line)] last:border-0 ${
          onRowClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5' : ''
        }`}
      >
        {columns.map((col) => (
          <td
            key={col.key}
            style={col.width ? { width: col.width } : undefined}
            className={`px-3 py-2.5 ${ALIGN_CLASS[col.align ?? 'left']} ${col.cellClassName ?? ''}`}
          >
            {col.cell(row)}
          </td>
        ))}
      </tr>
      {renderAfterRow?.(row)}
    </Fragment>
  ));
  const renderColgroup = () => (
    <colgroup>
      {columns.map((col) => (
        <col key={col.key} style={col.width ? { width: col.width } : undefined} />
      ))}
    </colgroup>
  );
  const hasWidths = columns.some((col) => Boolean(col.width));

  const tableShell = pinHeader ? (
    <div
      className={`${TABLE_PINNED[breakpoint]} panel-table-wrap panel-table-wrap--pinned min-h-0 flex-1`}
      data-testid="responsive-data-list-table"
    >
      <div className="panel-thead shrink-0 overflow-hidden border-b border-[var(--bi-line)] [scrollbar-gutter:stable]">
        <table className="w-full table-fixed text-left text-sm">
          {renderColgroup()}
          <thead className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {headerRow}
          </thead>
        </table>
      </div>
      <div
        className="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable]"
        data-testid="responsive-data-list-table-body"
      >
        <table className="w-full table-fixed text-left text-sm" aria-label={ariaLabel}>
          {renderColgroup()}
          <tbody>{bodyRows}</tbody>
        </table>
      </div>
    </div>
  ) : (
    <div
      className={`${TABLE_VISIBILITY[breakpoint]} panel-table-wrap`}
      data-testid="responsive-data-list-table"
    >
      <table
        className={`w-full text-left text-sm ${hasWidths ? 'table-fixed' : 'min-w-full'}`}
        aria-label={ariaLabel}
      >
        {hasWidths ? renderColgroup() : null}
        <thead className="panel-thead border-b border-[var(--bi-line)] text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {headerRow}
        </thead>
        <tbody>{bodyRows}</tbody>
      </table>
    </div>
  );

  return (
    <div className={className}>
      {tableShell}

      {onSortChange && sortableColumns.length > 0 ? (
        <div
          className={`${CARDS_VISIBILITY[breakpoint]} mb-2 flex items-center gap-2`}
          data-testid="responsive-data-list-sort"
        >
          <label className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('common.sortBy')}
            </span>
            <select
              value={activeSort.key}
              onChange={(event) =>
                onSortChange({ key: event.target.value, direction: activeSort.direction })
              }
              className="panel-focus min-w-0 flex-1 truncate rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
            >
              {sortableColumns.map((col) => (
                <option key={col.key} value={col.key}>
                  {col.sortLabel ?? (typeof col.header === 'string' ? col.header : col.key)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() =>
              onSortChange({ key: activeSort.key, direction: isAsc ? 'desc' : 'asc' })
            }
            className="panel-touch panel-focus shrink-0 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 dark:border-gray-700 dark:text-slate-200"
            aria-label={isAsc ? t('common.sortAsc') : t('common.sortDesc')}
          >
            <span aria-hidden>{isAsc ? '↑' : '↓'}</span>
          </button>
        </div>
      ) : null}

      <ul
        className={`${CARDS_VISIBILITY[breakpoint]} min-h-0 flex-1 space-y-3 overflow-y-auto`}
        data-testid="responsive-data-list-cards"
      >
        {rows.map((row) => (
          <li
            key={keyField(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={onRowClick ? (event) => handleRowKeyDown(row, event) : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            className={`panel-list-card p-3 ${onRowClick ? 'cursor-pointer' : ''}`}
          >
            {card(row)}
          </li>
        ))}
      </ul>
    </div>
  );
}
